import React from 'react';
import styled from 'styled-components';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import { Text } from '@solvhealth/jigsaw-v1';
import { useSelector } from 'react-redux';
import { useLDFlags } from '~/core/launch-darkly/hooks/useLDFlags';
import { colors } from '../../../constants/colors';
import { fontSize } from '../../../constants/text';
import { biggerThan, smallerThanOrEqualTo } from '../../../core/util/styledComponents';
import AutocompleteIcon from '../AutocompleteIcon';
import SecondaryLabel from './SecondaryLabel';
import { AutoCompleteResult } from '../AutocompleteResults';
import { analyticsTrackEvent } from '~/core/analytics';
import { UNIVERSAL_AUTOSUGGEST_ITEM, ACTION_CLICK } from '~/core/analytics/events';
import { SolvReduxState } from '~/reducers';
import { isAStructuredRfv } from '../util';

type Props = {
  label: 'What?' | 'Where?' | 'When?' | 'Who?' | 'Location' | '';

  /** The results to display */
  results?: AutoCompleteResult[];

  showingDefaultResults?: boolean;
};

const Suggestion = styled.div.withConfig({ componentId: 'sc-1395nch-0' })`
  ${smallerThanOrEqualTo.sm`
    margin-left: 10px;
  `}
`;

const Icon = styled(AutocompleteIcon).withConfig({ componentId: 'sc-1395nch-1' })`
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  ${biggerThan.sm`
    margin-right: 10px;
  `}
`;

const Item = styled(ListItem).withConfig({ componentId: 'sc-1395nch-2' })<{
  $narrow?: boolean;
  $arrow?: boolean;
  $hoverEffect?: boolean;
  $floatRight?: boolean;
}>`
  padding: 10px;
  display: flex;
  min-height: ${(props) => (props.$narrow ? '20' : '50')}px;
  transition: background-color 100ms ease;
  padding-left: ${(props) => (props.$floatRight ? '32px' : '24px')};
`;

const Primary = styled.div.withConfig({ componentId: 'sc-1395nch-3' })`
  color: ${colors.myRightFoot};
  font-size: ${fontSize.default};
  line-height: 20px;

  & em {
    color: ${colors.crunchBerry};
    font-style: normal;
  }
`;

/**
 * A list of autocomplete results or dropdown options to display on the homepage search.
 * Supports images and icons.
 */
const Results = ({ label, results, showingDefaultResults }: Props) => {
  const { structuredReasonForVisit } = useLDFlags();
  const { universalSearch } = useSelector((state: SolvReduxState) => ({
    universalSearch: state.universalSearch,
  }));

  const handleClick = (result: any) => {
    if (result.static) {
      return;
    }

    if (label === 'What?') {
      analyticsTrackEvent(UNIVERSAL_AUTOSUGGEST_ITEM, {
        action: ACTION_CLICK,
        query: universalSearch.query,
        value: result?.fullDoc?.name,
        autosuggests: results?.map((r) => r?.fullDoc?.name),
        structuredRfvTestGroup: structuredReasonForVisit ? 'test' : 'control',
        isQueryAStructuredRfv: isAStructuredRfv(universalSearch.query, universalSearch.results),
      });
    }

    if (result.onClick) {
      result.onClick();
    }
  };

  return structuredReasonForVisit ? (
    <List>
      {showingDefaultResults && (
        <Text fontWeight="bold" margin="16px 0px 8px 24px">
          Common searches
        </Text>
      )}
      {results?.map((result, i) => (
        <Item
          $floatRight={showingDefaultResults}
          $narrow={result.narrow}
          button={!result.static as true}
          data-testid={`auto-complete-${result.primary.split(' ').join('-')}${
            result.secondary ? `-${result?.secondary.split(' ').join('-')}` : ''
          }`}
          key={i}
          onClick={() => handleClick(result)}
        >
          <Suggestion>
            {result.primaryNode ? (
              result.primaryNode
            ) : (
              <Primary dangerouslySetInnerHTML={{ __html: result.primary }} />
            )}
            <SecondaryLabel result={result} />
          </Suggestion>
        </Item>
      ))}
    </List>
  ) : (
    <List>
      {results?.map((result, i) => (
        <Item
          $narrow={result.narrow}
          button={!result.static as true}
          data-testid={`auto-complete-${result.primary.split(' ').join('-')}${
            result.secondary ? `-${result?.secondary.split(' ').join('-')}` : ''
          }`}
          key={i}
          onClick={() => handleClick(result)}
        >
          <Icon suggestion={result} />
          <Suggestion>
            {result.primaryNode ? (
              result.primaryNode
            ) : (
              <Primary dangerouslySetInnerHTML={{ __html: result.primary }} />
            )}
            <SecondaryLabel result={result} />
          </Suggestion>
        </Item>
      ))}
    </List>
  );
};

export default Results;
